import { useEffect, useRef, useState } from 'react';
import { Briefcase, Loader2, ToggleLeft, ToggleRight } from 'lucide-react';
import { hiringLinksAPI, type HiringSettingsView } from '../../services/api';
import { HiringMessagesLine } from './HiringMessagesLine';
import { CANCEL_SWITCH, RAISE_SWITCH, cancelErrorText, formatHiringDay, hiringCodeOf, hiringErrorText, hiringStatusOf, raiseErrorText } from './hiringText';
import { clearHiringAccessCache } from './useHiringAccess';

/**
 * Project setting: show hiring for this project. A super admin turns it on here; until
 * then every hiring route answers "not enabled" for the project and the Stores page
 * looks as it always did. Saves at once, like the switch it is — there is nothing else
 * on the card to save with it.
 *
 * THIS CARD IS THE SWITCH. It is here for a super admin from the day Hiring is deployed,
 * with nothing added on the server: the server is on unless it has been told to stop
 * (HIRING_LINK_ENABLED=false, the emergency off).
 *
 * A second switch, "Raise hiring from ChannelPlex", is drawn under it. It too is here
 * from the first day with nothing added on the server, and it is off for every project.
 * It is THE switch of Raise - and turning it ON makes real openings possible, so it is
 * never one stray click: the switch only opens a second step, in the style of the Stores
 * page's Remove confirmation, and nothing is saved until "Yes, turn on Raise". Turning it
 * off asks nothing. Raise needs Show on: its switch is disabled, with the reason, while
 * Show is off, and Show cannot be turned off while Raise is on.
 *
 * A third switch, "Cancel hiring from ChannelPlex", is drawn under Raise, in the same
 * way and for the same reason: it is THE switch of Cancel, here from the first day with
 * nothing added on the server, and off for every project. Turning it ON lets real
 * openings be DELETED in Recruitment, so it too only opens a second step, and nothing is
 * saved until "Yes, turn on Cancel". Turning it off is one click. Cancel needs Show on,
 * and NOT Raise: an opening linked by hand can be cancelled in a project that never
 * raises. Show cannot be turned off while Cancel is on. Switching an opening to another
 * store is not built.
 *
 * The settings answer says whether Raise has been stopped on the server
 * (`raiseAvailableOnServer: false`, the emergency off HIRING_LINK_RAISE_ENABLED=false).
 * Then the card says so, and the switch cannot be turned on - only off, for a project
 * that had it on. An answer that does not carry the field at all is a server without
 * Raise: the card is then exactly the one-switch card it was. The same holds for Cancel
 * (`changeAvailableOnServer`, the emergency off HIRING_LINK_CHANGE_ENABLED=false): without
 * the field no Cancel switch is drawn.
 *
 * Draws nothing at all while Hiring is stopped on the server (the read then answers
 * 404), on a network failure, and before the answer: Budget Settings is then exactly as
 * it was. A 403 is different — it only comes back when Hiring is not stopped, so the
 * card shows the server's reason instead of leaving a super admin to wonder where the
 * switch went.
 */
export function HiringSeeSwitchCard({ projectId }: { projectId: number }) {
  const [settings, setSettings] = useState<HiringSettingsView | null>(null);
  // The server's sentence when the read was refused (403); the card then has no switch.
  const [refused, setRefused] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [raiseError, setRaiseError] = useState<string | null>(null);
  const [raiseNotice, setRaiseNotice] = useState<string | null>(null);
  // The second step before Raise is turned on. Nothing is saved while it is open.
  const [confirmingRaise, setConfirmingRaise] = useState(false);
  const raiseSwitchRef = useRef<HTMLButtonElement>(null);
  // Set when the second step closes: focus went with the button that was pressed in it.
  const refocusRaise = useRef(false);
  // The same three for Cancel: what its save said, and its own second step.
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelNotice, setCancelNotice] = useState<string | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const cancelSwitchRef = useRef<HTMLButtonElement>(null);
  const refocusCancel = useRef(false);

  useEffect(() => {
    let alive = true;
    setSettings(null);
    setRefused(null);
    setError(null);
    setNotice(null);
    setRaiseError(null);
    setRaiseNotice(null);
    setConfirmingRaise(false);
    setCancelError(null);
    setCancelNotice(null);
    setConfirmingCancel(false);
    // Inside the chain: a throw while building the request must never reach the page.
    Promise.resolve()
      .then(() => hiringLinksAPI.getSettings(projectId))
      .then((res) => {
        if (alive && res?.data?.data) setSettings(res.data.data);
      })
      .catch((err: unknown) => {
        if (alive && hiringStatusOf(err) === 403 && hiringCodeOf(err)) {
          setRefused(hiringErrorText(err, 'Could not read the Hiring switch.'));
        }
      });
    return () => {
      alive = false;
    };
  }, [projectId]);

  // Back on the switch once the second step has closed and the switch can take focus again.
  useEffect(() => {
    if (confirmingRaise || !refocusRaise.current) return;
    refocusRaise.current = false;
    raiseSwitchRef.current?.focus();
  }, [confirmingRaise, saving]);
  useEffect(() => {
    if (confirmingCancel || !refocusCancel.current) return;
    refocusCancel.current = false;
    cancelSwitchRef.current?.focus();
  }, [confirmingCancel, saving]);

  if (!settings && !refused) return null;

  const on = settings?.readEnabled === true;
  // The server has Raise at all: it says, as a plain yes or no, whether Raise is available.
  const raiseShown = typeof settings?.raiseAvailableOnServer === 'boolean';
  // "No" is the emergency off on the server: nothing can be sent, and it cannot be turned on.
  const raiseStopped = settings?.raiseAvailableOnServer === false;
  const raiseOn = settings?.raiseEnabled === true;
  // Whether the Raise switch can be moved: off is always allowed; on needs Show on and no stop.
  const raiseMovable = raiseOn || (on && !raiseStopped);
  // The server has Cancel at all, whether it is stopped there, and whether it is on for this project.
  const cancelShown = typeof settings?.changeAvailableOnServer === 'boolean';
  const cancelStopped = settings?.changeAvailableOnServer === false;
  const cancelOn = cancelShown && settings?.changeEnabled === true;
  // Off is always allowed; on needs Show on and no stop. It does not need Raise.
  const cancelMovable = cancelOn || (on && !cancelStopped);
  // Show cannot go off under a Raise or a Cancel that is on: the server would refuse it.
  const heldByRaise = raiseShown && on && raiseOn;
  const heldByCancel = on && cancelOn;
  const showHeldOn = heldByRaise || heldByCancel;

  const saveRaise = async (next: boolean) => {
    if (!settings || saving || !raiseShown) return;
    // On is saved only from the second step, and only where it can be on at all.
    if (next && (!confirmingRaise || !on || raiseStopped)) return;
    setSaving(true);
    setRaiseError(null);
    setRaiseNotice(null);
    try {
      const res = await hiringLinksAPI.setRaiseEnabled(projectId, next);
      setSettings(res?.data?.data ?? { ...settings, raiseEnabled: next });
      // Every screen that asked what may be done here asks again.
      clearHiringAccessCache();
      setRaiseNotice(next ? RAISE_SWITCH.turnedOn : RAISE_SWITCH.turnedOff);
    } catch (err) {
      setRaiseError(raiseErrorText(err, RAISE_SWITCH.saveFailed));
    } finally {
      setSaving(false);
      if (next) {
        refocusRaise.current = true;
        setConfirmingRaise(false);
      }
    }
  };

  /**
   * The Raise switch was pressed. Off: saved at once, as Show is. ON: nothing is saved -
   * the second step opens, and only its "Yes, turn on Raise" saves.
   */
  const pressRaise = () => {
    if (!settings || saving || confirmingRaise || !raiseShown || !raiseMovable) return;
    if (raiseOn) {
      void saveRaise(false);
      return;
    }
    setRaiseError(null);
    setRaiseNotice(null);
    // One question at a time: a question about Cancel that was open is withdrawn.
    setConfirmingCancel(false);
    setConfirmingRaise(true);
  };

  const saveCancel = async (next: boolean) => {
    if (!settings || saving || !cancelShown) return;
    // On is saved only from the second step, and only where it can be on at all.
    if (next && (!confirmingCancel || !on || cancelStopped)) return;
    setSaving(true);
    setCancelError(null);
    setCancelNotice(null);
    try {
      const res = await hiringLinksAPI.setChangeEnabled(projectId, next);
      setSettings(res?.data?.data ?? { ...settings, changeEnabled: next });
      // Every screen that asked what may be done here asks again.
      clearHiringAccessCache();
      setCancelNotice(next ? CANCEL_SWITCH.turnedOn : CANCEL_SWITCH.turnedOff);
    } catch (err) {
      setCancelError(cancelErrorText(err, CANCEL_SWITCH.saveFailed));
    } finally {
      setSaving(false);
      if (next) {
        refocusCancel.current = true;
        setConfirmingCancel(false);
      }
    }
  };

  /**
   * The Cancel switch was pressed. Off: saved at once. ON: nothing is saved - the second
   * step opens, and only its "Yes, turn on Cancel" saves.
   */
  const pressCancel = () => {
    if (!settings || saving || confirmingCancel || !cancelShown || !cancelMovable) return;
    if (cancelOn) {
      void saveCancel(false);
      return;
    }
    setCancelError(null);
    setCancelNotice(null);
    setConfirmingRaise(false);
    setConfirmingCancel(true);
  };

  const flip = async () => {
    if (!settings || saving || showHeldOn) return;
    const next = !on;
    // A question about Raise or Cancel that was open is withdrawn: with Show off there is nothing to turn on.
    setConfirmingRaise(false);
    setConfirmingCancel(false);
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await hiringLinksAPI.setReadEnabled(projectId, next);
      setSettings(res?.data?.data ?? { ...settings, readEnabled: next });
      // Every screen that asked "is hiring on here?" asks again.
      clearHiringAccessCache();
      setNotice(next
        ? 'The Hiring column is now shown for this project.'
        : 'The Hiring column is now hidden for this project. Links already made are kept. Nothing changed in Recruitment.');
    } catch (err) {
      // The switch was never moved on screen, so it is already back at its saved position.
      setError(hiringErrorText(err, 'Could not save the Hiring switch. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section aria-labelledby="hiring-see-switch-title" className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <Briefcase className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden />
        <div className="min-w-0 flex-1 space-y-2">
          <div>
            <h2 id="hiring-see-switch-title" className="text-sm font-semibold text-gray-900">Hiring</h2>
            <p className="text-xs text-gray-500">
              Shows Dashboard › Hiring for this project, and a small Hiring column on its Stores page, to the roles
              with the Hiring tab ticked. People can link an opening that already exists in Recruitment, for a store or
              with no store, and see how the hiring is going.
              {/* True only while Raise and Cancel are both off for the project: with either on, real openings are created or deleted from here. */}
              {!raiseOn && !cancelOn && ' Nothing is sent to Recruitment.'}
            </p>
          </div>
          {refused ? (
            <p className="text-xs text-red-700" role="alert">{refused}</p>
          ) : (
            <>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                aria-label="Show hiring for this project"
                onClick={() => void flip()}
                disabled={saving || showHeldOn}
                className="inline-flex items-center gap-1.5 text-sm font-medium disabled:opacity-60"
              >
                {on ? <ToggleRight className="h-6 w-6 text-green-500" /> : <ToggleLeft className="h-6 w-6 text-gray-400" />}
                <span className="text-gray-700">Show hiring for this project</span>
                <span className={on ? 'text-green-700' : 'text-gray-400'}>{on ? 'On' : 'Off'}</span>
              </button>
              <p className="text-xs text-gray-500">
                Saves as soon as you switch it. Give roles access in Access Management: tick Hiring (under Dashboard) for them.
              </p>
              {saving && <p className="text-xs text-gray-500">Saving…</p>}
              {settings?.updatedAt && !saving && (
                <p className="text-xs text-gray-500">
                  Last changed{settings.updatedBy ? ` by ${settings.updatedBy}` : ''} on {formatHiringDay(settings.updatedAt)}.
                </p>
              )}
              {notice && <p className="text-xs text-green-700" role="status">{notice}</p>}
              {error && <p className="text-xs text-red-700" role="alert">{error}</p>}
              {heldByRaise && <p className="text-xs text-gray-500">{RAISE_SWITCH.seeHeldOn}</p>}
              {heldByCancel && <p className="text-xs text-gray-500">{CANCEL_SWITCH.seeHeldOn}</p>}

              {raiseShown && (
                <div className="space-y-2 border-t border-gray-100 pt-3">
                  <p className="text-xs text-gray-500">{RAISE_SWITCH.about}</p>
                  <button
                    ref={raiseSwitchRef}
                    type="button"
                    role="switch"
                    aria-checked={raiseOn}
                    aria-label={RAISE_SWITCH.label}
                    onClick={pressRaise}
                    disabled={saving || confirmingRaise || !raiseMovable}
                    className="inline-flex items-center gap-1.5 text-sm font-medium disabled:opacity-60"
                  >
                    {raiseOn ? <ToggleRight className="h-6 w-6 text-green-500" /> : <ToggleLeft className="h-6 w-6 text-gray-400" />}
                    <span className="text-gray-700">{RAISE_SWITCH.label}</span>
                    <span className={raiseOn ? 'text-green-700' : 'text-gray-400'}>{raiseOn ? 'On' : 'Off'}</span>
                  </button>
                  {raiseStopped && <p className="text-xs text-amber-700">{RAISE_SWITCH.stoppedOnServer}</p>}
                  {!raiseStopped && !on && !raiseOn && <p className="text-xs text-gray-500">{RAISE_SWITCH.needsSee}</p>}
                  {/* Two clicks to turn Raise ON, as Remove on the Stores page: the switch
                      only asks, and nothing is saved until "Yes, turn on Raise". */}
                  {confirmingRaise && (
                    <div
                      role="group"
                      aria-label={RAISE_SWITCH.confirmGroupLabel}
                      className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3"
                    >
                      {RAISE_SWITCH.confirmLines.map((line) => (
                        <p key={line} className="text-xs font-medium text-gray-700">{line}</p>
                      ))}
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Focus lands on the safe answer. */}
                        <button
                          type="button"
                          autoFocus
                          onClick={() => {
                            refocusRaise.current = true;
                            setConfirmingRaise(false);
                          }}
                          disabled={saving}
                          className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                        >
                          {RAISE_SWITCH.confirmNo}
                        </button>
                        <button
                          type="button"
                          onClick={() => void saveRaise(true)}
                          disabled={saving}
                          className="inline-flex items-center gap-1 rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                        >
                          {saving && <Loader2 className="h-3 w-3 animate-spin" />}
                          {RAISE_SWITCH.confirmYes}
                        </button>
                      </div>
                    </div>
                  )}
                  {raiseNotice && <p className="text-xs text-green-700" role="status">{raiseNotice}</p>}
                  {raiseError && <p className="text-xs text-red-700" role="alert">{raiseError}</p>}
                </div>
              )}

              {cancelShown && (
                <div className="space-y-2 border-t border-gray-100 pt-3">
                  <p className="text-xs text-gray-500">{CANCEL_SWITCH.about}</p>
                  <button
                    ref={cancelSwitchRef}
                    type="button"
                    role="switch"
                    aria-checked={cancelOn}
                    aria-label={CANCEL_SWITCH.label}
                    onClick={pressCancel}
                    disabled={saving || confirmingCancel || !cancelMovable}
                    className="inline-flex items-center gap-1.5 text-sm font-medium disabled:opacity-60"
                  >
                    {cancelOn ? <ToggleRight className="h-6 w-6 text-green-500" /> : <ToggleLeft className="h-6 w-6 text-gray-400" />}
                    <span className="text-gray-700">{CANCEL_SWITCH.label}</span>
                    <span className={cancelOn ? 'text-green-700' : 'text-gray-400'}>{cancelOn ? 'On' : 'Off'}</span>
                  </button>
                  {cancelStopped && <p className="text-xs text-amber-700">{CANCEL_SWITCH.stoppedOnServer}</p>}
                  {!cancelStopped && !on && !cancelOn && <p className="text-xs text-gray-500">{CANCEL_SWITCH.needsSee}</p>}
                  {/* Two clicks to turn Cancel ON: the switch only asks, and nothing is
                      saved until "Yes, turn on Cancel". */}
                  {confirmingCancel && (
                    <div
                      role="group"
                      aria-label={CANCEL_SWITCH.confirmGroupLabel}
                      className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3"
                    >
                      {CANCEL_SWITCH.confirmLines.map((line) => (
                        <p key={line} className="text-xs font-medium text-gray-700">{line}</p>
                      ))}
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Focus lands on the safe answer. */}
                        <button
                          type="button"
                          autoFocus
                          onClick={() => {
                            refocusCancel.current = true;
                            setConfirmingCancel(false);
                          }}
                          disabled={saving}
                          className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                        >
                          {CANCEL_SWITCH.confirmNo}
                        </button>
                        <button
                          type="button"
                          onClick={() => void saveCancel(true)}
                          disabled={saving}
                          className="inline-flex items-center gap-1 rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                        >
                          {saving && <Loader2 className="h-3 w-3 animate-spin" />}
                          {CANCEL_SWITCH.confirmYes}
                        </button>
                      </div>
                    </div>
                  )}
                  {cancelNotice && <p className="text-xs text-green-700" role="status">{cancelNotice}</p>}
                  {cancelError && <p className="text-xs text-red-700" role="alert">{cancelError}</p>}
                </div>
              )}
              {/* Whether Recruitment's real-time messages are being accepted on this server. One line; it switches nothing. */}
              <HiringMessagesLine projectId={projectId} />
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default HiringSeeSwitchCard;
